import React, { useEffect, useState } from 'react';
import { Menu, X } from 'lucide-react';
import { fetchMenu } from '../utils/api';

/**
 * Header component for the Old World Treasures website.
 * Manages site title, logo, and main navigation, including mobile menu toggle.
 * @param {object} props - The component props.
 * @param {string} props.currentPage - The current page in the App.
 * @param {function} props.setCurrentPage - Function to change the current page in the App.
 * @param {boolean} props.isMobileMenuOpen - State indicating if the mobile menu is open.
 * @param {function} props.setIsMobileMenuOpen - Function to toggle the mobile menu state.
 */
const Header = ({ currentPage, navigateTo, isMobileMenuOpen, setIsMobileMenuOpen }) => {
  const [menu, setMenu] = useState([]);
  const [loadingData, setLoadingData] = useState(true);
  const [errorData, setErrorData] = useState(null);

  useEffect(() => {
    document.title = process.env.REACT_APP_TITLE;
    
    const getMenu = async () => {
      try {
        setLoadingData(true);
        const menuData = await fetchMenu();

        const navItems = [
          { path: '/', name: 'Home' },
          ...menuData.map(item => ({ path: `/${item}`, name: item })),
          { path: '/about', name: 'About Us' },
          { path: '/services', name: 'Related Services' },
          { path: '/contact', name: 'Contact Us' }
        ];

        setMenu(navItems);
      } catch (error) {
        setErrorData("Failed to load menu.");
      } finally {
        setLoadingData(false);
      }
    };
    
    getMenu();
  }, []);

  if (loadingData) return <div className="text-center text-text-dark text-xl mt-8">Loading data...</div>;
  if (errorData) return <div className="text-center text-red-500 text-xl mt-8">{errorData}</div>;

  return (
    <header className="bg-primary-dark text-header-footer-text p-4 shadow-lg rounded-b-lg">
      <div className="container mx-auto flex flex-col md:flex-row justify-between items-center">
        {/* Logo and Title */}
        <div className="flex items-center justify-between w-full md:w-auto mb-2 md:mb-0">
          <div className="flex items-center">
            {/* <img
              src="/logo.gif" // Placeholder for your logo
              alt="Rare Finds"
              className="h-14 w-14 rounded-full mr-3 shadow-md"
              onError={(e) => { e.target.onerror = null; e.target.src = "https://placehold.co/60x60/FFFFFF/000000?text=Logo"; }}
            /> */}
            <h1 className="text-4xl font-extrabold tracking-tight">
              {process.env.REACT_APP_TITLE}
            </h1>
          </div>
          {/* Mobile menu toggle button */}
          <button
            className="md:hidden text-header-footer-text focus:outline-none"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="Toggle navigation menu"
          >
            {isMobileMenuOpen ? <X size={32} /> : <Menu size={32} />}
          </button>
        </div>

        {/* Navigation - hidden on small screens by default, shown on md and up, or when mobile menu is open */}
        <nav className={`${isMobileMenuOpen ? 'block' : 'hidden'} md:block w-full md:w-auto mt-4 md:mt-0`}>
          <ul className="flex flex-col md:flex-row items-center md:space-x-6 space-y-2 md:space-y-0 text-lg">
            
            {menu && menu.map((link) => (
              <li key={link.path}>
                <button
                  onClick={() => { navigateTo(link.path); setIsMobileMenuOpen(false); }}
                  className="hover:text-accent-main transition duration-300 flex items-center bg-transparent border-none cursor-pointer p-0 w-full justify-center text-menu-text"
                >
                  <span className={`${link.path === currentPage ? 'text-accent-main' : ''}`}>
                    {link.name}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </nav>
      </div>
    </header>
  );
};

export default Header;
